import { Euler, Quaternion, Vector3 } from "three";
import { Enemy } from "@objects/entities";
import { MapConfig } from "@objects/terrain";
import { MathUtils } from "@utils/math";
class EnemyManager {
    static instance;
    enemies;
    /**
     * EnemyManager holds all the enemies in the game.
     * @param amount the amount of enemies to spawn.
     * @param scene the rendering scene.
     * @param physicsWorld the physics world.
     * @private
     */
    constructor(amount = 10, scene, physicsWorld) {
        this.enemies = this.spawnEnemies(amount, scene, physicsWorld);
    }
    /**
     * Perform a step on all enemies.
     * @param scene the rendering scene.
     * @param physicsWorld the physics world.
     * @param targets A list of targets to be checked when the enemy is shooting.
     * @param terrain The terrain, used for checks when shooting.
     * @param deltaTime The interval in seconds from the last frame to the current one.
     */
    step(scene, physicsWorld, targets, terrain, deltaTime) {
        for (const enemy of this.enemies) {
            if (this.isDead(enemy, scene, physicsWorld))
                continue;
            enemy.step(targets, terrain, deltaTime);
            this.handleShooting(enemy, targets, terrain);
        }
    }
    /**
     * Check whether or not an enemy is dead, and handle it if it is.
     * @param enemy The enemy to check for death.
     * @param scene The scene to potentially remove the enemy from if it is dead.
     * @param physicsWorld The physics world to potentially remove the enemy from if it is dead.
     * @returns A boolean indicating whether or not the enemy is dead.
     */
    isDead(enemy, scene, physicsWorld) {
        if (enemy.isDead) {
            const index = this.enemies.findIndex(_enemy => _enemy === enemy);
            enemy.kill(scene, physicsWorld);
            this.enemies.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Handle shooting on an enemy.
     * @param enemy The enemy which is potentially shooting.
     * @param targets The possible targets that the enemy can hit.
     * @param terrain The terrain, so that enemies behind terrain don't get hit.
     */
    handleShooting(enemy, targets, terrain) {
        if (!enemy.getInputController.getSHOOTING)
            return;
        const shootingOriginPosition = new Vector3();
        const shootingOriginDirection = new Vector3();
        enemy.getGun.getSkeleton.muzzle.getBone.getWorldPosition(shootingOriginPosition);
        enemy.getGun.getSkeleton.muzzle.getBone.getWorldDirection(shootingOriginDirection);
        shootingOriginDirection.negate();
        enemy.getGun.shoot(targets, terrain, shootingOriginPosition, shootingOriginDirection);
    }
    /**
     * Spawn enemies in the world.
     * @param amount The amount of enemies to spawn.
     * @param scene the rendering scene.
     * @param physicsWorld the physics world.
     * @returns An array containing the spawned enemies.
     * @private
     */
    spawnEnemies(amount, scene, physicsWorld) {
        const enemies = new Array();
        for (let index = 0; index < amount; index++) {
            enemies.push(this.spawnEnemy(scene, physicsWorld));
        }
        return enemies;
    }
    /**
     * Spawn an enemy.
     * @param scene the rendering scene.
     * @param physicsWorld the physics world.
     * @param position An optional position to spawn the enemy at. If no value is provided, a random position will be chosen on the map.
     * @param rotation An optional rotation to spawn the enemy with. If no value is provided, a random rotation will be chosen.
     * @returns The newly spawned enemy.
     */
    spawnEnemy(scene, physicsWorld, position, rotation) {
        const getPosition = (min, max, padding) => {
            min += padding;
            max -= padding;
            return MathUtils.randRange(min, max);
        };
        const _position = position
            ? position
            : new Vector3(getPosition(-(MapConfig.GROUND_WIDTH / 2), MapConfig.GROUND_WIDTH / 2, 10), 10, getPosition(-(MapConfig.GROUND_HEIGHT / 2), MapConfig.GROUND_HEIGHT / 2, 10));
        const _rotation = rotation
            ? rotation
            : new Quaternion().setFromEuler(new Euler(0, MathUtils.randRange(-Math.PI, Math.PI), 0));
        return new Enemy(scene, physicsWorld, _position, _rotation);
    }
    get getEnemies() {
        return this.enemies;
    }
    /**
     * Get or create an enemy manager.
     * @param scene the rendering scene.
     * @param physicsWorld the physics world.
     * @param amount the amount of enemies to spawn.
     * @returns An instance of an enemy manager.
     */
    static getInstance(scene, physicsWorld, amount) {
        if (!EnemyManager.instance)
            EnemyManager.instance = new EnemyManager(amount, scene, physicsWorld);
        return EnemyManager.instance;
    }
}
export default EnemyManager;
